import { __assign, __rest } from "tslib";
import React, { useRef, useState } from 'react';
import styles from './styles.css.js';
import { normalizeLocale } from '../date-picker/calendar/utils/locales';
import { getWeekStartByLocale } from 'weekstart';
import useForwardFocus from '../internal/hooks/forward-focus';
import { KeyCode } from '../internal/keycode';
import clsx from 'clsx';
import { getBaseProps } from '../internal/base-component';
import checkControlled from '../internal/hooks/check-controlled';
import InternalBox from '../box/internal';
import { DateRangePickerDropdown } from './dropdown';
import { applyDisplayName } from '../internal/utils/apply-display-name';
import Dropdown from '../internal/components/dropdown';
import { useFocusTracker } from '../internal/hooks/use-focus-tracker';
import { useMobile } from '../internal/hooks/use-mobile';
import ButtonTrigger from '../internal/components/button-trigger';
import { useFormFieldContext } from '../internal/context/form-field-context';
import InternalIcon from '../icon/internal';
import { getBrowserTimezoneOffset, shiftTimeoffset, formatOffset } from './time-offset';
import useBaseComponent from '../internal/hooks/use-base-component';
import mergeRefs from 'react-merge-refs';
function formatDateRange(range, placeholder, formatRelativeRange, timeOffset) {
    if (!range) {
        return (React.createElement("span", { className: styles['label-text'], "aria-disabled": true }, placeholder));
    }
    if (range.type === 'relative') {
        return (React.createElement(InternalBox, { fontWeight: "normal", display: "inline", color: "inherit" }, formatRelativeRange(range)));
    }
    if (range.type === 'absolute') {
        var formattedOffset = formatOffset(timeOffset);
        return (React.createElement(InternalBox, { fontWeight: "normal", display: "inline", color: "inherit" },
            React.createElement("span", { className: styles['preferred-wordbreak'] },
                range.startDate,
                formattedOffset,
                " \u2014"),
            ' ',
            React.createElement("span", { className: styles['preferred-wordbreak'] },
                range.endDate,
                formattedOffset)));
    }
}
var DateRangePicker = React.forwardRef(function (_a, ref) {
    var _b;
    var _c = _a.locale, locale = _c === void 0 ? '' : _c, startOfWeek = _a.startOfWeek, isDateEnabled = _a.isDateEnabled, value = _a.value, placeholder = _a.placeholder, _d = _a.readOnly, readOnly = _d === void 0 ? false : _d, _e = _a.disabled, disabled = _e === void 0 ? false : _e, onChange = _a.onChange, onBlur = _a.onBlur, onFocus = _a.onFocus, _f = _a.relativeOptions, relativeOptions = _f === void 0 ? [] : _f, i18nStrings = _a.i18nStrings, isValidRange = _a.isValidRange, _g = _a.showClearButton, showClearButton = _g === void 0 ? true : _g, _h = _a.timeOffset, timeOffset = _h === void 0 ? getBrowserTimezoneOffset() : _h, _j = _a.timeInputFormat, timeInputFormat = _j === void 0 ? 'hh:mm:ss' : _j, rest = __rest(_a, ["locale", "startOfWeek", "isDateEnabled", "value", "placeholder", "readOnly", "disabled", "onChange", "onBlur", "onFocus", "relativeOptions", "i18nStrings", "isValidRange", "showClearButton", "timeOffset", "timeInputFormat"]);
    var __internalRootRef = useBaseComponent('DateRangePicker').__internalRootRef;
    checkControlled('DateRangePicker', 'value', value, 'onChange', onChange);
    var timeOffsetCorrectedValue = shiftTimeoffset(value, timeOffset);
    var baseProps = getBaseProps(rest);
    var _k = useFormFieldContext(rest), invalid = _k.invalid, controlId = _k.controlId, ariaDescribedby = _k.ariaDescribedby, ariaLabelledby = _k.ariaLabelledby;
    var isSingleGrid = useMobile();
    var triggerRef = useRef(null);
    useForwardFocus(ref, triggerRef);
    var rootRef = useRef(null);
    useFocusTracker({ rootRef: rootRef, onBlur: onBlur, onFocus: onFocus });
    var _l = useState(false), isDropDownOpen = _l[0], setIsDropDownOpen = _l[1];
    var normalizedLocale = normalizeLocale('DateRangePicker', locale !== null && locale !== void 0 ? locale : '');
    var normalizedStartOfWeek = (typeof startOfWeek === 'number'
        ? startOfWeek % 7
        : getWeekStartByLocale(normalizedLocale));
    var closeDropdown = function () {
        var _a;
        setIsDropDownOpen(false);
        (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var onWrapperKeyDownHandler = function (event) {
        if (event.keyCode === KeyCode.escape) {
            closeDropdown();
        }
    };
    var trigger = (React.createElement("div", { className: styles['trigger-wrapper'] },
        React.createElement(ButtonTrigger, { ref: triggerRef, id: controlId, invalid: invalid, ariaDescribedby: ariaDescribedby, ariaLabelledby: ariaLabelledby, className: clsx(styles.label, (_b = {},
                _b[styles['label-enabled']] = !readOnly && !disabled,
                _b)), hideCaret: true, onClick: function () {
                if (!readOnly && !disabled) {
                    setIsDropDownOpen(true);
                }
            }, disabled: disabled, readOnly: readOnly, ariaHasPopup: "true" },
            React.createElement("span", { className: styles['trigger-flexbox'] },
                React.createElement("span", { className: styles['icon-wrapper'] },
                    React.createElement(InternalIcon, { name: "calendar", variant: disabled || readOnly ? 'disabled' : 'normal' })),
                formatDateRange(timeOffsetCorrectedValue, placeholder !== null && placeholder !== void 0 ? placeholder : '', i18nStrings.formatRelativeRange, timeOffset)))));
    return (React.createElement("div", __assign({}, baseProps, { ref: mergeRefs([rootRef, __internalRootRef]), className: clsx(baseProps.className, styles.root), onKeyDown: onWrapperKeyDownHandler }),
        React.createElement(Dropdown, { stretchWidth: true, stretchHeight: true, open: isDropDownOpen, onDropdownClose: closeDropdown, trigger: trigger, stretchToTriggerWidth: false }, isDropDownOpen && (React.createElement(DateRangePickerDropdown, { startOfWeek: normalizedStartOfWeek, locale: normalizedLocale, isSingleGrid: isSingleGrid, onDropdownClose: closeDropdown, value: timeOffsetCorrectedValue, showClearButton: showClearButton, isDateEnabled: isDateEnabled !== null && isDateEnabled !== void 0 ? isDateEnabled : (function () { return true; }), i18nStrings: i18nStrings, onChange: onChange !== null && onChange !== void 0 ? onChange : (function () { return void 0; }), relativeOptions: relativeOptions, isValidRange: isValidRange !== null && isValidRange !== void 0 ? isValidRange : (function () { return ({ valid: true }); }), timeOffset: timeOffset, timeInputFormat: timeInputFormat })))));
});
applyDisplayName(DateRangePicker, 'DateRangePicker');
export default DateRangePicker;
//# sourceMappingURL=index.js.map